class AmPmHero extends HTMLElement {
  connectedCallback() {
    this._stickyToggle = document.getElementById('AmPmStickyToggle');
    this._toggleWrap = this.querySelector('.am-pm-hero__toggle-wrap');
    this._downArrow = this.querySelector('.am-pm-hero__down-arrow');
    this._allButtons = [
      ...this.querySelectorAll('.am-pm-toggle__btn'),
      ...(this._stickyToggle?.querySelectorAll('.am-pm-toggle__btn') ?? [])
    ];

    this._restoreMode();
    this._syncAriaState();
    this._initToggleButtons();
    this._initObserver();
    this._initDownArrow();
    this._setHeroHeight();

    this._resizeHandler = this._setHeroHeight.bind(this);
    window.addEventListener('resize', this._resizeHandler);

    this._pageshowHandler = (e) => {
      if (e.persisted) {
        this._restoreMode();
        this._syncAriaState();
        // Kill transitions before snapping to the correct state — iOS bfcache
        // leaves opacity stuck mid-transition, making the toggle invisible.
        if (this._toggleWrap) this._toggleWrap.style.transition = 'none';
        if (this._stickyToggle) this._stickyToggle.style.transition = 'none';
        this._scrollHandler?.();
        requestAnimationFrame(() => {
          if (this._toggleWrap) this._toggleWrap.style.transition = '';
          if (this._stickyToggle) this._stickyToggle.style.transition = '';
        });
      }
    };
    window.addEventListener('pageshow', this._pageshowHandler);
  }

  disconnectedCallback() {
    this._observer?.disconnect();
    window.removeEventListener('resize', this._resizeHandler);
    window.removeEventListener('scroll', this._scrollHandler);
    window.removeEventListener('pageshow', this._pageshowHandler);
  }

  _setHeroHeight() {
    if (window.matchMedia('(max-width: 991px)').matches) {
      this.style.minHeight = '';
      return;
    }
    if (window.innerWidth - this.offsetWidth > 20) {
      this.style.minHeight = '';
      return;
    }
    const header = document.querySelector('.header-wrapper') || document.querySelector('header');
    const announcementBar = document.querySelector('.utility-bar') || document.querySelector('.announcement-bar');
    const headerHeight = header ? header.getBoundingClientRect().height : 0;
    const announcementHeight = announcementBar ? announcementBar.getBoundingClientRect().height : 0;
    this.style.minHeight = `calc(100svh - ${headerHeight + announcementHeight}px)`;
  }

  _restoreMode() {
    const saved = sessionStorage.getItem('ampm-mode');
    if (saved && saved !== this._currentMode) {
      document.body.classList.remove('ampm-mode-am', 'ampm-mode-pm');
      document.body.classList.add(`ampm-mode-${saved}`);
    }
  }

  get _currentMode() {
    return document.body.classList.contains('ampm-mode-pm') ? 'pm' : 'am';
  }

  _syncAriaState() {
    const mode = this._currentMode;
    this._allButtons.forEach((btn) => {
      btn.setAttribute('aria-pressed', (btn.dataset.mode === mode).toString());
    });
  }

  _initToggleButtons() {
    this._allButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const { mode } = btn.dataset;
        const prevMode = this._currentMode;
        document.body.classList.remove('ampm-mode-am', 'ampm-mode-pm');
        document.body.classList.add(`ampm-mode-${mode}`);
        sessionStorage.setItem('ampm-mode', mode);
        this._syncAriaState();
        this._switchHeroVideos(prevMode, mode);
        document.dispatchEvent(
          new CustomEvent('ampm:mode-change', { detail: { mode } })
        );
      });
    });
  }

  _switchHeroVideos(fromMode, toMode) {
    this.querySelectorAll(`.am-pm-hero__bg-${fromMode} video`).forEach((v) => v.pause());
    this.querySelectorAll(`.am-pm-hero__bg-${toMode} video`).forEach((v) => {
      if (v.preload === 'none') {
        v.preload = 'auto';
        v.load();
      }
      v.play().catch(() => {});
    });
  }

  _initObserver() {
    if (!this._stickyToggle) return;

    // Show sticky toggle on first scroll; hide in-hero toggle simultaneously
    this._scrollHandler = () => {
      const scrolled = window.scrollY > 0;
      this._stickyToggle.classList.toggle('is-visible', scrolled);
      this._stickyToggle.setAttribute('aria-hidden', (!scrolled).toString());
      this._toggleWrap?.classList.toggle('is-hidden', scrolled);
    };

    window.addEventListener('scroll', this._scrollHandler, { passive: true });
    // Run once on init in case the page is already scrolled
    this._scrollHandler();
  }

  _initDownArrow() {
    if (!this._downArrow) return;

    this._downArrow.addEventListener('click', (e) => {
      e.preventDefault();

      const { target } = this._downArrow.dataset;
      if (target) {
        document.getElementById(target)?.scrollIntoView({ behavior: 'smooth' });
      }
    });
  }
}

if (!customElements.get('am-pm-hero')) {
  customElements.define('am-pm-hero', AmPmHero);
}
